/*
 * What the backend decides about each platform, gathered in one place so the
 * listing, the "add" dialog and the detail page apply the same rules.
 */

/**
 * The platforms a seller can integrate, keyed by the slug the addresses use
 * (`/configuration/ecommerce/new/:platform`). `name` is the template's name in
 * `buildAvailableEcommerces`.
 *
 * The backend keeps no single spelling — the Shopify OAuth stores "shopify",
 * the template says "Shopify" — so names are always compared lower-cased.
 */
export const PLATFORMS = [
  { slug: 'shopify', name: 'Shopify' },
  { slug: 'woocommerce', name: 'Woocommerce' },
  { slug: 'prestashop', name: 'Prestashop' },
  { slug: 'mercado-libre', name: 'Mercado Libre' },
  { slug: 'bootic', name: 'Bootic' },
  { slug: 'vtex', name: 'VTex' },
];

export const SHOPIFY_SLUG = 'shopify';
export const MELI_SLUG = 'mercado-libre';

/**
 * @param {string} name - As stored or as in the template.
 * @returns {string|null}
 */
export const getPlatformSlug = (name) => {
  const lowerName = String(name || '').toLowerCase();
  return PLATFORMS.find((platform) => platform.name.toLowerCase() === lowerName)?.slug || null;
};

export const getPlatformBySlug = (slug) =>
  PLATFORMS.find((platform) => platform.slug === slug) || null;

/** "shopify" as stored, "Shopify" on screen. An unknown name is shown as it came. */
export const getPlatformDisplayName = (name) =>
  getPlatformBySlug(getPlatformSlug(name))?.name || name;

export const CONNECTION_STATUS = {
  CONNECTED: 'connected',
  PENDING: 'pending',
  LEGACY: 'legacy',
  CONFIGURED: 'configured',
};

export const CONNECTION_STATUS_LABELS = {
  [CONNECTION_STATUS.CONNECTED]: 'Conectada',
  [CONNECTION_STATUS.PENDING]: 'Autorización pendiente',
  [CONNECTION_STATUS.LEGACY]: 'Versión legacy',
  [CONNECTION_STATUS.CONFIGURED]: 'Configurada',
};

/**
 * How far an integration got, from what its configuration shows. Only the two
 * OAuth platforms leave a trace of it; the rest are "configured" once saved.
 *
 * - Shopify is connected when the callback stored an `shpat_` token and marked
 *   it authorized — the same test `ShopifyOauth` uses. With a client secret and
 *   no token, the authorization was started and not finished. Without a client
 *   secret it predates OAuth.
 * - Mercado Libre gets its `integration_user_id` only in the callback: the
 *   record exists, "initialized", from the moment the seller is sent away.
 *
 * @param {object} integration
 * @returns {string} One of `CONNECTION_STATUS`.
 */
export const getConnectionStatus = (integration) => {
  const authentication = integration?.configuration?.authentication || {};
  const slug = getPlatformSlug(integration?.name);

  if (slug === SHOPIFY_SLUG) {
    const hasToken = String(authentication.access_token || '').startsWith('shpat_');
    if (hasToken && authentication.authorized) return CONNECTION_STATUS.CONNECTED;
    if (authentication.client_secret) return CONNECTION_STATUS.PENDING;
    return CONNECTION_STATUS.LEGACY;
  }

  if (slug === MELI_SLUG) {
    return authentication.integration_user_id
      ? CONNECTION_STATUS.CONNECTED
      : CONNECTION_STATUS.PENDING;
  }

  return CONNECTION_STATUS.CONFIGURED;
};

/** A Mercado Libre integration whose authorization never came back. */
export const isMeliPending = (integration) =>
  getPlatformSlug(integration?.name) === MELI_SLUG &&
  !integration?.configuration?.authentication?.integration_user_id;

/**
 * Warehouses that can still take a Mercado Libre account: the backend accepts
 * one per warehouse (`set_meli_oauth`) and routes its orders by warehouse.
 */
export const getWarehousesWithoutMeli = (warehouses = [], integrations = []) => {
  const taken = new Set(
    integrations
      .filter((integration) => getPlatformSlug(integration.name) === MELI_SLUG)
      .map((integration) => String(integration.warehouse))
  );
  return warehouses.filter((warehouse) => !taken.has(String(warehouse.id)));
};

export const UNAVAILABLE_REASON = {
  INTEGRATED: 'integrated',
  ALL_WAREHOUSES: 'all-warehouses',
  NO_WAREHOUSES: 'no-warehouses',
};

/**
 * Whether each platform can be added, keyed by slug.
 *
 * One integration per platform per company: when orders arrive, the backend
 * looks the integration up with `.first()` by company and platform
 * (`orders/views.py`), so a second one of the same platform would never be
 * used. Mercado Libre is the exception, one per warehouse.
 *
 * @param {object} params
 * @param {Array<object>} params.integrations
 * @param {Array<object>} params.warehouses
 * @returns {Object.<string, {available: boolean, existingId: number|null, reason: string|null}>}
 */
export const getPlatformAvailability = ({ integrations = [], warehouses = [] }) =>
  PLATFORMS.reduce((availability, { slug }) => {
    if (slug === MELI_SLUG) {
      const freeWarehouses = getWarehousesWithoutMeli(warehouses, integrations);
      let reason = null;
      if (warehouses.length === 0) reason = UNAVAILABLE_REASON.NO_WAREHOUSES;
      else if (freeWarehouses.length === 0) reason = UNAVAILABLE_REASON.ALL_WAREHOUSES;
      availability[slug] = { available: reason === null, existingId: null, reason };
      return availability;
    }

    const existing = integrations.find((integration) => getPlatformSlug(integration.name) === slug);
    availability[slug] = {
      available: !existing,
      existingId: existing ? existing.id : null,
      reason: existing ? UNAVAILABLE_REASON.INTEGRATED : null,
    };
    return availability;
  }, {});

/**
 * The warehouses a platform form may offer. The forms are the legacy's and are
 * not touched, so the two backend constraints arrive through this prop:
 *
 * - **Shopify on OAuth, when editing**: only its own warehouse. Its init does
 *   `update_or_create` by warehouse, so reconnecting from another warehouse
 *   would create a second Shopify — and the callback breaks with two.
 * - **Mercado Libre, when adding**: only warehouses without one.
 *
 * @param {object} params
 * @param {object} params.integration - Stored or template.
 * @param {boolean} params.isEdit
 * @param {Array<object>} params.warehouses
 * @param {Array<object>} params.integrations
 */
export const getSelectableWarehouses = ({ integration, isEdit, warehouses = [], integrations = [] }) => {
  const slug = getPlatformSlug(integration?.name);

  if (slug === SHOPIFY_SLUG && isEdit && integration?.configuration?.authentication?.client_secret) {
    return warehouses.filter((warehouse) => String(warehouse.id) === String(integration.warehouse));
  }

  if (slug === MELI_SLUG && !isEdit) {
    return getWarehousesWithoutMeli(warehouses, integrations);
  }

  return warehouses;
};
